<template>
    <div>

        <v-container fluid class="pt-0">

            <v-row dense>

                <v-col cols="4">

                    <v-switch
                            color="primary"
                            v-model="mySharedButtonModel"
                            label="Show mine"
                            hide-details
                    ></v-switch>
                </v-col>

                <v-col cols="4" offset="4">

                    <v-text-field
                            class="mr-5"
                            v-model="searchModel"
                            prepend-icon="mdi-magnify"
                            label="Search"
                            single-line
                            hide-details
                            clearable
                    ></v-text-field>
                </v-col>
            </v-row>

        </v-container>

        <!-- TABLE CONTENTS -->
        <v-data-table
                :headers="headers"
                :items="segmentsDisplay"
                :loading="loading"
                :footer-props="{itemsPerPageOptions:[10,25,50,100,-1]}"
                sort-by="updated_at"
                :sort-desc="true"
                :search="searchModel"
        >
            <template v-slot:body="{ items }">

                <tbody>

                <!-- NO DATA INFO -->
                <tr v-if="items.length===0 && !loading">
                    <td colspan="6">
                        <v-alert
                                color="primary"
                                class="mt-4 body-2"
                                icon="mdi-information">
                            No segments found
                        </v-alert>
                    </td>
                </tr>

                <!-- ITEMS -->
                <tr v-for="item in items" :key="item.id" class="cursor-pointer">

                    <td>
                        <avatar :image-url="item.created_by.photo_url" :person-name="item.created_by.name">
                            {{item.created_by.name}}
                        </avatar>
                    </td>

                    <td @click="nameClicked(item)" class="cursor-pointer">
                        {{item.name}}
                    </td>

                    <!--- TYPE -->
                    <td>
                        <v-tooltip top>
                            <template v-slot:activator="{ on }">
                                <v-chip pill v-on="on" color="primary" text-color="white">
                                    <v-icon small v-if="item.type==='filter'">mdi-filter</v-icon>
                                    <v-icon small v-if="item.type==='query'">mdi-magnify</v-icon>
                                    {{ucFirst(item.type)}}
                                </v-chip>
                            </template>

                            <span>ID: {{item.id}}</span>
                        </v-tooltip>
                    </td>

                    <!-- UPDATED AT -->
                    <td>
                        <v-tooltip top color="primary">
                            <template v-slot:activator="{ on }">
                                <div class="text-no-wrap" v-on="on">
                                    {{ toHumanTill(item.updated_at) }}
                                </div>
                            </template>

                            <div class="body-2 ma-2">
                                <v-icon class="mr-2">mdi-clock</v-icon>
                                {{ toLocalDateTime(item.updated_at) }}
                            </div>
                        </v-tooltip>
                    </td>

                    <!-- ACTIONS -->
                    <td v-if="!selectOnly">

                        <div style="white-space: nowrap;">

                            <v-tooltip top color="primary">
                                <template v-slot:activator="{ on }">
                                    <v-btn :disabled="!hasPermission('run queries')"
                                           class="elevation-0 ma-0"
                                           color="primary"
                                           v-on="on"
                                           x-small
                                           fab
                                           @click="nameClicked(item)">

                                        <v-icon light color="white">mdi-play</v-icon>
                                    </v-btn>
                                </template>
                                <span>Run this segment</span>
                            </v-tooltip>
                        </div>
                    </td>
                </tr>
                </tbody>
            </template>

            <!-- NO DATA SLOT -->
            <template v-slot:no-data>
                <v-alert class="body-2"
                         color="primary"
                         icon="mdi-information">
                    No segments found
                </v-alert>
            </template>

            <!-- NO RESULTS SLOT -->
            <template v-slot:no-results>
                <v-alert class="body-2"
                         color="primary"
                         icon="mdi-information">
                    No segments found
                </v-alert>
            </template>

        </v-data-table>
    </div>
</template>

<script>

    import {mapState, mapMutations, mapGetters, mapActions} from 'vuex';
    import {timeDisplay} from '../../../components/mixins/timeDisplayMixin';
    import Avatar from '../../../components/UI/Avatar';
    import Json from 'vue-json-pretty';

    export default {

        components: {
            Avatar,
            Json
        },

        mixins: [timeDisplay],

        props: {
            selectOnly: {
                required: false,
                type: Boolean,
                default: false,
            },
            filtersOnly: {
                required: false,
                type: Boolean,
                default: false,
            }
        },

        data() {
            return {

                highlighted: null,

                mySharedButtonModel: true,
                searchModel: null,

                headersBase: [
                    {
                        text: 'OWNER',
                        value: 'created_by.name',
                        width: '100px'
                    },
                    {
                        text: 'NAME',
                        align: 'left',
                        value: 'name'
                    },
                    {
                        text: 'TYPE',
                        align: 'left',
                        value: 'type',
                        width: '130px'
                    },
                    {
                        text: 'LAST MODIFIED',
                        align: 'left',
                        value: 'updated_at',
                        width: '200px'
                    }
                ],
            }
        },

        computed: {

            ...mapState('Segments', [
                'segments',
                'segmentsLoading'
            ]),

            headers() {
                let headers = [...this.headersBase];

                if (!this.selectOnly) {
                    headers.push({
                        text: 'ACTIONS',
                        align: 'left',
                        sortable: false,
                        width: '85px'
                    });
                }

                return headers;
            },

            segmentsDisplay() {
                if (this.mySharedButtonModel) {
                    return this.segments.filter((def) => {

                        if (def.shared) {
                            // Return only filters if so requested
                            if (this.filtersOnly) {
                                return def.type === 'filter';
                            }
                            return true;
                        }
                        return false;
                    });
                } else {
                    return this.segments.filter((def) => {
                        // Filter out user's own segments
                        if (def.created_by.id !== this.$store.state.user.id) {
                            // Return only filters if so requested
                            if (this.filtersOnly) {
                                return def.type === 'filter';
                            }
                            return true;
                        }
                        return false;
                    });
                }
            },
            loading() {
                return this.segmentsLoading;
            }
        },

        methods: {
            nameClicked(definition) {
                if (this.selectOnly) {
                    this.$emit("select", definition);
                } else {
                    this.$emit("edit", definition);
                }
            },
        },

        created() {
            if (!this.$store.state.Segments) {
                this.$store.registerModule('Segments', Store);
            }
        },
    }
</script>
